/*
 * ReferenceLibrary.tsx — S57: Reference Library Browser
 * API: GET /api/v1/library/documents, GET /api/v1/library/index
 */
import { useState, useEffect } from 'react';
import { API_V1, safeFetch } from '../constants';

interface LibDoc {
  id: number;
  doc_type?: string;
  document_type?: string;
  standard_code?: string;
  document_code?: string;
  standard_name?: string;
  title?: string;
  framework: string;
  issuing_body: string;
  jurisdiction?: string;
  effective_date?: string | null;
  description?: string;
}

export default function ReferenceLibrary() {
  const [docs, setDocs] = useState<LibDoc[]>([]);
  const [indexCount, setIndexCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('ALL');

  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([
      safeFetch(`${API_V1}/library/documents`, { documents: [] }),
      safeFetch(`${API_V1}/library/index`, { count: 0 }),
    ])
      .then(([docRes, idxRes]) => {
        setDocs(docRes.documents || []);
        setIndexCount(idxRes.count || 0);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="main" style={{ padding: 32 }}><p style={{ color: 'var(--text-secondary)' }}>Loading reference library...</p></div>;
  if (error) return (
    <div className="main" style={{ padding: 32 }}>
      <p style={{ color: 'var(--red)' }}>Error: {error}</p>
      <button onClick={load} style={{ marginTop: 8 }}>Retry</button>
    </div>
  );

  const frameworks = ['ALL', ...Array.from(new Set(docs.map(d => d.framework)))];
  const filtered = filter === 'ALL' ? docs : docs.filter(d => d.framework === filter);
  const grouped: Record<string, LibDoc[]> = {};
  for (const d of filtered) {
    const key = d.framework;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(d);
  }

  return (
    <div className="main" style={{ padding: 32, maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 className="page-title" style={{ marginBottom: 0 }}>Reference Library</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {frameworks.map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{
                padding: '4px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                background: filter === f ? 'var(--accent)' : 'var(--bg-surface)',
                color: filter === f ? '#fff' : 'var(--text-secondary)',
                border: 'none',
              }}>
              {f}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <div className="card" style={{ padding: 16, textAlign: 'center', flex: 1 }}>
          <div className="stitle" style={{ marginBottom: 4 }}>Documents</div>
          <div className="mono" style={{ fontSize: 24, fontWeight: 700 }}>{docs.length}</div>
        </div>
        <div className="card" style={{ padding: 16, textAlign: 'center', flex: 1 }}>
          <div className="stitle" style={{ marginBottom: 4 }}>Index Entries</div>
          <div className="mono" style={{ fontSize: 24, fontWeight: 700 }}>{indexCount}</div>
        </div>
        <div className="card" style={{ padding: 16, textAlign: 'center', flex: 1 }}>
          <div className="stitle" style={{ marginBottom: 4 }}>Frameworks</div>
          <div className="mono" style={{ fontSize: 24, fontWeight: 700 }}>{frameworks.length - 1}</div>
        </div>
      </div>

      {Object.entries(grouped).sort().map(([fw, list]) => (
        <div key={fw} className="card" style={{ padding: 20, marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--accent)' }}>{fw}</h3>
          {list.sort((a, b) => (a.document_code || a.standard_code || '').localeCompare(b.document_code || b.standard_code || '')).map(d => (
            <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <div>
                <span className="mono" style={{ fontSize: 13, color: 'var(--text-primary)', marginRight: 8 }}>{d.document_code || d.standard_code}</span>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{d.title || d.standard_name || ''}</span>
              </div>
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{d.issuing_body}</span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 8 }}>{list.length} standards</div>
        </div>
      ))}

      {filtered.length === 0 && (
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <p style={{ color: 'var(--text-tertiary)' }}>No documents found for this filter.</p>
        </div>
      )}
    </div>
  );
}
